import { Chips, Css, RightPanePanel, useComputed, useModal, useRightPaneActions, useTestIds } from "@homebound/beam";
import { useFormState } from "@homebound/form-state";
import { useEffect } from "react";
import { useTakeoffsBulkEditPanel_GetTlisQuery } from "src/routes/libraries/plan-package-v2/components/takeoffs/side-panel/TakeoffsBulkEditPanel.generated";
import {
  BulkEditFields,
  bulkEditFormConfig,
  saveBulkEdit,
} from "src/routes/libraries/plan-package/takeoffs/components/BulkEditFields";
import { DeleteItemsConfirmationModal } from "src/routes/libraries/plan-package/takeoffs/components/DeleteItemsConfirmationModal";
import {
  useSelectedTliIds,
  useTakeoffsManagerContext,
  useTakeoffsStore,
} from "src/routes/libraries/plan-package/takeoffs/TakeoffsManagerContext";
import { queryResult } from "src/utils";

/** Bulk edits the table's selection, following it as rows are toggled. Only changed fields are applied. */
export function TakeoffsBulkEditPanel() {
  const store = useTakeoffsManagerContext();
  const planPackageId = useTakeoffsStore((state) => state.planPackage.id);
  const versionId = useTakeoffsStore((state) => state.planPackage.version.id);
  const itemTableApi = useTakeoffsStore((state) => state.itemTableApi);
  const selectedTliIds = useSelectedTliIds(itemTableApi);
  const { closeRightPane } = useRightPaneActions();
  const { openModal } = useModal();
  const tid = useTestIds({}, "takeoffsBulkEditPanel");

  const query = useTakeoffsBulkEditPanel_GetTlisQuery({
    variables: { filter: { id: selectedTliIds, readyPlan: [planPackageId] }, versionId },
    skip: selectedTliIds.isEmpty,
  });
  // Lags the selection while a toggle re-queries, so Save waits for `loading` to clear
  const tlis = (query.data ?? query.previousData)?.takeoffLineItemsPage.items ?? [];

  const formState = useFormState({ config: bulkEditFormConfig });
  const dirty = useComputed(() => formState.dirty, [formState]);

  // Clearing the selection, or a delete (whose save clears it), leaves nothing to edit. The table can empty it several
  // ways, so this follows the selection. Prevent the unsaved-changes prompt by reverting first
  const isEmpty = selectedTliIds.isEmpty;
  useEffect(() => {
    if (!isEmpty) return;
    formState.revertChanges();
    closeRightPane();
  }, [isEmpty, formState, closeRightPane]);

  async function save() {
    await saveBulkEdit({ formState, saveTli: store.getState().saveTli, tlis });
    closeRightPane();
  }

  const costCodes = tlis.map((tli) => tli.item.costCode.displayName).unique();

  return (
    <RightPanePanel
      title="Bulk Edit"
      isDirty={() => formState.dirty}
      deleteAction={{
        count: selectedTliIds.length,
        onClick: () =>
          openModal({ content: <DeleteItemsConfirmationModal store={store} itemIdsToDelete={selectedTliIds} /> }),
      }}
      secondaryAction={{ label: "Cancel", onClick: closeRightPane }}
      primaryAction={{ label: "Save", disabled: query.loading || (!dirty && "Change a field to save"), onClick: save }}
    >
      {queryResult(query, () => (
        <div css={Css.df.fdc.gap2.$}>
          <div {...tid.costCodes}>
            <Chips values={costCodes} />
          </div>
          <BulkEditFields store={store} formState={formState} tlis={tlis} />
        </div>
      ))}
    </RightPanePanel>
  );
}
